/**
 * @file 全局主题状态
 * @description 使用 Pinia 持久化主题模式，并解析跟随系统时的实际主题。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/** 主题模式：浅色 / 暗色 / 跟随系统 */
export type ThemeMode = 'light' | 'dark' | 'system'

/** 解析后的实际主题（不含 system） */
export type ResolvedTheme = 'light' | 'dark'

/**
 * 根据系统偏好解析实际主题
 */
function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

/**
 * 全局主题状态（Pinia 持久化）
 */
export const themeStore = defineStore(
  'tdm-themeStore',
  () => {
    /** 用户选择的主题模式 */
    const mode = ref<ThemeMode>('system')

    /** 当前实际生效的主题（system 已解析） */
    const resolvedTheme = computed<ResolvedTheme>(() => {
      if (mode.value === 'system') {
        return resolveSystemTheme()
      }
      return mode.value
    })

    /** 是否处于暗色模式 */
    const isDark = computed(() => resolvedTheme.value === 'dark')

    /**
     * 设置主题模式
     * @param nextMode 目标模式
     */
    const setMode = (nextMode: ThemeMode): void => {
      mode.value = nextMode
    }

    /**
     * 循环切换：light → dark → system
     */
    const cycleMode = (): void => {
      const order: ThemeMode[] = ['light', 'dark', 'system']
      const index = order.indexOf(mode.value)
      const next = order[(index + 1) % order.length]
      setMode(next)
    }

    return {
      mode,
      resolvedTheme,
      isDark,
      setMode,
      cycleMode
    }
  },
  {
    persist: {
      key: 'tdm-theme-mode',
      paths: ['mode']
    }
  }
)
